import { formatSectionAndFieldName } from './helpers';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _kmq: any[];
  }
}

export class Tracker {
  private static instance: Tracker;
  private constructor() {}

  /*    
    Event nomenclature goes a long way. 
    
    When adding new events, try to:

    - Use prefixes to groups related events. 
    - Understandable names. 
      app-table-tab-change vs 'nav-change'
    - Decouple from things that might change, CTA copy. 
      'application-begin' vs 'clicked-get-started' 
  */
  public static EVENTS = {
    // User and account
    LOGOUT: 'user-logout',
    LOGIN: 'user-login',
    MAGIC_LINK_LOGIN: 'user-login-magic-link',
    REQUEST_LOGIN_MAGIC_LINK: 'user-request-login-magic-link',
    FORGOT_PASSWORD: 'user-forgot-password',
    REGISTER_CLICKED: 'user-register-clicked',
    REGISTER_SUCCESS: 'user-register-success',
    REGISTER_ERROR: 'user-register-error',

    // Navbar
    NAVBAR_ITEM_CLICKED: 'navbar-item-clicked',

    // New applicant invites
    NEW_APPLICANT_ERROR: 'new-applicant-error',
    NEW_APPLICANT_SUBMIT: 'new-applicant-submit',
    NEW_APPLICANT_CANCEL: 'new-applicant-cancel',
    NEW_APPLICANT_CTA_CLICKED: 'new-applicant-cta-clicked',
    NEW_APPLICANT_BACK_CLICKED: 'new-applicant-back-clicked',
    NEW_APPLICANT_SOLAR_CALC_SUCCESS: 'new-applicant-solar-calc-success',
    NEW_APPLICANT_SOLAR_CALC_ERROR: 'new-applicant-solar-calc-error',
    NEW_APPLICANT_FILE_UPLOAD_RETRY: 'new-applicant-file-upload-retry',
    NEW_APPLICANT_FILE_UPLOAD_ERROR: 'new-applicant-file-upload-error',
    NEW_APPLICANT_FILE_UPLOAD_SUCCESS: 'new-applicant-file-upload-success',

    // Applications table
    APP_TABLE_NAV_CHANGE: 'app-table-tab-change',
    APP_TABLE_ROW_EXPAND: 'app-table-row-expand',
    APP_TABLE_ROW_COLLAPSE: 'app-table-row-collapse',

    // Pricing calculator
    PRICING_CALCULATOR_BACK: 'pricing-calculator-back',
    PRICING_CALCULATOR_SUBMIT: 'pricing-calculator-submit',
    PRICING_CALCULATOR_ERROR: 'pricing-calculator-error',

    // This is to track the usage of the info tooltip modals on each loan type button
    PRICING_CALCULATOR_LOAN_TYPE_MODAL_OPENED:
      'pricing-calculator-loan-type-modal-opened',

    PRICING_CALCULATOR_LOAN_SUMMARY_DRAWER_USED_IN_MOBILE:
      'pricing-calculator-loan-summary-drawer-used-in-mobile',

    PRICING_CALCULATOR_MONTHLY_PAYMENT_CALCULATED:
      'pricing-calculator-monthly-payment-calculated',

    // Price and Prequalify Calculator Hover events:
    PRICING_CALCULATOR_SPECIAL_PAYMENT_TOOLTIP:
      'pricing-calculator-special-payment-tooltip',
    PRICING_CALCULATOR_MONTHLY_PAYMENT_TOOLTIP:
      'pricing-calculator-monthly-payment-tooltip',
    PRICING_CALCULATOR_NET_FUNDING_TOOLTIP:
      'pricing-calculator-net-funding-tooltip',
    PRICING_CALCULATOR_TOTAL_LOAN_AMOUNT_TOOLTIP:
      'pricing-calculator-total-loan-amount-tooltip',

    // Prequalify calc section events
    PREQUALIFY_CALC_SUCCESS: 'prequalify-calc-success',
    PREQUALIFY_CALC_ERROR: 'prequalify-calc-error',

    // Quote page events
    QUOTE_DOWNLOAD_PDF_CLICKED: 'quote-download-pdf-clicked',
    QUOTE_INITIALIZE_APP_CLICKED: 'quote-initialize-app-clicked',
    QUOTE_VIEW_RATE_SHEETS_CLICKED: 'quote-view-rate-sheets-clicked',

    // Projects table
    PROJECT_TABLE_NAV_CHANGE: 'project-table-tab-change',

    VIEW_AMORTIZATION_SCHEDULE: 'view-amortization-schedule',

    // Field time tracking
    FIELD_FOCUSED: (name: string) =>
      `${formatSectionAndFieldName(name)}-field-focused`,
    FIELD_BLURRED: (name: string) =>
      `${formatSectionAndFieldName(name)}-field-blurred`,

    // Section time tracking

    // Need to format the name to match the format in the tracker
    SECTION_ENTERED: (titleId: string) =>
      `loan-app-section-entered-${formatSectionAndFieldName(titleId)}`,
    SECTION_EXITED: (titleId: string) =>
      `loan-app-section-exited-${formatSectionAndFieldName(titleId)}`,
    SECTION_CHANGED: (titleId: string) =>
      `loan-app-sidebar-clicked-${formatSectionAndFieldName(titleId)}`,
    SECTION_CONTINUED: (titleId: string) =>
      `loan-app-section-clicked-continue-${formatSectionAndFieldName(titleId)}`,
    SECTION_BACK: (titleId: string) =>
      `loan-app-section-clicked-back-${formatSectionAndFieldName(titleId)}`,
    SECTION_SAVED_FOR_LATER: (titleId: string) =>
      `loan-app-section-clicked-save-for-later-${formatSectionAndFieldName(
        titleId
      )}`,
    SECTION_SAVED_FOR_LATER_CONFIRMED: (titleId: string) =>
      `loan-app-section-clicked-save-for-later-confirmed-${formatSectionAndFieldName(
        titleId
      )}`,
    SECTION_LEFT_TO_DASHBOARD: 'loan-app-section-clicked-dashboard',
    COMPLETED_APPLICATION: 'loan-app-section-completed-application',

    // Resources page
    TRAINING_VIDEO_CLICKED: 'training-video-clicked',

    // Document Analysis
    ORACLE_DOC_ANALYSIS_REQUEST: 'oracle-doc-analysis-request',
    ORACLE_DOC_ANALYSIS_REQUEST_SUCCESS: 'oracle-doc-analysis-request-success',
    ORACLE_DOC_ANALYSIS_REQUEST_FAILED: 'oracle-doc-analysis-request-failed',
    ORACLE_DOC_ANALYSIS_VALIDATION_DOC_RECOGNIZED_TRUE:
      'oracle-doc-analysis-validation-doc-recognized-true',
    ORACLE_DOC_ANALYSIS_VALIDATION_DOC_RECOGNIZED_FALSE:
      'oracle-doc-analysis-validation-doc-recognized-false',
    ORACLE_DOC_ANALYSIS_REQUEST_INVALID_RESPONSE_FORMAT:
      'oracle-doc-analysis-request-invalid-response-format',

    // User management
    USER_CREATED: 'user-created',
    USER_UPDATED: 'user-updated',
    USER_DEACTIVATED: 'user-deactivated',
    USER_REACTIVATED: 'user-reactivated',
    USER_RESENT_INVITATION: 'user-resent-invitation',
    USER_RESENT_VERIFICATION: 'user-resent-verification',

    // Network status
    NETWORK_RECONNECTED: 'network-reconnected',
  };

  public static getInstance(): Tracker {
    if (!Tracker.instance) {
      Tracker.instance = new Tracker();
    }
    return Tracker.instance;
  }

  public track(event: string, properties?: Record<string, any>): void {
    const eventProperties = {
      ...properties,
      rosefieldVersion: __versionString__,
    };

    if (typeof window._kmq !== 'undefined') {
      window._kmq.push(['record', event, eventProperties]);
    } else {
      console.error('_kmq is not defined');
    }
  }

  public trackFieldFocus = (name: string, flow: string) => {
    // We normalize the name to remove the dot notation (i.e.: principals[0].fieldname)
    const normalizedName = name.match(/[^.]+$/)?.[0] || name;
    this.track(Tracker.EVENTS.FIELD_FOCUSED(normalizedName), { flow });
  };

  public trackFieldBlur = (name: string, flow: string) => {
    // We normalize the name to remove the dot notation (i.e.: principals[0].fieldname)
    const normalizedName = name.match(/[^.]+$/)?.[0] || name;
    this.track(Tracker.EVENTS.FIELD_BLURRED(normalizedName), { flow });
  };

  public trackSectionEntered = (titleId: string, flow: string) => {
    this.track(Tracker.EVENTS.SECTION_ENTERED(titleId), { flow });
  };

  public trackSectionChanged = (titleId: string, flow: string) => {
    this.track(Tracker.EVENTS.SECTION_CHANGED(titleId), { flow });
  };

  public trackSectionContinued = (titleId: string, flow: string) => {
    this.track(Tracker.EVENTS.SECTION_CONTINUED(titleId), { flow });
  };

  public trackSectionBack = (titleId: string, flow: string) => {
    this.track(Tracker.EVENTS.SECTION_BACK(titleId), { flow });
  };

  public trackSectionSavedForLater = (titleId: string, flow: string) => {
    this.track(Tracker.EVENTS.SECTION_SAVED_FOR_LATER(titleId), { flow });
  };

  public trackSectionSavedForLaterConfirmed = (
    titleId: string,
    flow: string
  ) => {
    this.track(Tracker.EVENTS.SECTION_SAVED_FOR_LATER_CONFIRMED(titleId), {
      flow,
    });
  };

  public trackSectionLeftToDashboard = (flow: string) => {
    this.track(Tracker.EVENTS.SECTION_LEFT_TO_DASHBOARD, { flow });
  };

  public trackCompletedApplication = (flow: string) => {
    this.track(Tracker.EVENTS.COMPLETED_APPLICATION, { flow });
  };

  public trackSectionExited = (titleId: string, flow: string) => {
    this.track(Tracker.EVENTS.SECTION_EXITED(titleId), { flow });
  };

  public trackTrainingVideoClicked = (videoId: string, videoName: string) => {
    this.track(Tracker.EVENTS.TRAINING_VIDEO_CLICKED, {
      videoId,
      videoName,
    });
  };

  public identify(email: string): void {
    if (typeof window._kmq !== 'undefined') {
      window._kmq.push(['identify', email]);
    } else {
      console.error('_kmq is not defined');
    }
  }
}
